import styled from '@emotion/styled';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { Fragment } from 'react';

import { CobrainerLogo } from './assets/CobrainerLogo';
import { LanguageSelect } from './components/LanguageSelect';
import { RootStep, SidebarStep } from './SidebarStep';
import { useCompanyOnboardingStore } from './use-company-onboarding-store';

const Sidebar = styled.div({
    backgroundColor: '#f6f6f7',
    display: 'flex',
    flexDirection: 'column',
    gap: '2.5rem',
    padding: '1.5rem',
});

const SidebarTitle = styled.h2({
    color: '#6e6e6c',
    fontFamily: "'FT Regola Neue'",
    fontFeatureSettings: "'ss03' on",
    fontSize: '1.5rem',
    fontStyle: 'normal',
    fontWeight: 500,
    letterSpacing: '-0.03rem',
    lineHeight: '2rem',
});

const SidebarSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const LanguageSelectContainer = styled.div({
    marginTop: 'auto',
});

const SidebarShell: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const setSelectedLanguage = useCompanyOnboardingStore((state) => state.setSelectedLanguage);
    const selectedLanguage = useCompanyOnboardingStore((state) => state.selectedLanguage);
    const availableLanguages = useCompanyOnboardingStore((state) => state.availableLanguages);
    const setupCobrainerLabel = useCompanyOnboardingStore((state) => state.setupCobrainerLabel);

    return (
        <Sidebar>
            <CobrainerLogo />
            <SidebarTitle>{setupCobrainerLabel}</SidebarTitle>
            {children}
            <LanguageSelectContainer>
                <LanguageSelect
                    availableLanguages={availableLanguages}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                />
            </LanguageSelectContainer>
        </Sidebar>
    );
};

const NavigationSidebar: FunctionComponent = () => {
    const companyOnboardingStep = useCompanyOnboardingStore((state) => state.companyOnboardingStep);
    const companyDescription = useCompanyOnboardingStore((state) => state.companyDescription);
    const companyPriorities = useCompanyOnboardingStore((state) => state.companyPriorities);

    const companyProfileIsActive =
        companyOnboardingStep === 'company-description' || companyOnboardingStep === 'company-priorities';
    const companyProfileIsComplete = companyDescription.isJust && companyPriorities.isJust;

    const employeeProfileIsActive = companyOnboardingStep === 'cv-upload';

    const companyProfileLabel = useCompanyOnboardingStore((state) => state.companyProfileLabel);
    const companyDescriptionLabel = useCompanyOnboardingStore((state) => state.companyDescriptionLabel);
    const companyPrioritiesLabel = useCompanyOnboardingStore((state) => state.companyPrioritiesLabel);
    const employeeProfileLabel = useCompanyOnboardingStore((state) => state.employeeProfileLabel);
    const inviteYourTeamLabel = useCompanyOnboardingStore((state) => state.inviteYourTeamLabel);
    const skillMosaicLabel = useCompanyOnboardingStore((state) => state.skillMosaicLabel);
    const reviewSkillMosaicLabel = useCompanyOnboardingStore((state) => state.reviewSkillMosaicLabel);

    return (
        <Fragment>
            <SidebarSection>
                <RootStep
                    isActive={companyProfileIsActive}
                    isComplete={companyProfileIsComplete}
                    label={companyProfileLabel}
                    stepNumber={1}
                />
                <SidebarStep
                    isActive={companyOnboardingStep === 'company-description'}
                    isComplete={companyDescription.isJust}
                    label={companyDescriptionLabel}
                />
                <SidebarStep
                    isActive={companyOnboardingStep === 'company-priorities'}
                    isComplete={companyPriorities.isJust}
                    isDisabled={companyDescription.isNothing}
                    label={companyPrioritiesLabel}
                />
            </SidebarSection>
            <SidebarSection>
                <RootStep
                    isActive={employeeProfileIsActive}
                    isDisabled={companyProfileIsComplete === false}
                    label={employeeProfileLabel}
                    stepNumber={2}
                />
                <SidebarStep
                    isActive={companyOnboardingStep === 'cv-upload'}
                    isDisabled={companyProfileIsComplete === false}
                    label={skillMosaicLabel}
                />
                <SidebarStep
                    isActive={companyOnboardingStep === 'review-skills'}
                    isDisabled={companyProfileIsComplete === false}
                    label={reviewSkillMosaicLabel}
                />
            </SidebarSection>
            <SidebarSection>
                <RootStep isDisabled={true} label={inviteYourTeamLabel} stepNumber={3} />
            </SidebarSection>
        </Fragment>
    );
};

const EmployeeNavigationSidebar: FunctionComponent = () => {
    const companyOnboardingStep = useCompanyOnboardingStore((state) => state.companyOnboardingStep);
    const companyDescription = useCompanyOnboardingStore((state) => state.companyDescription);
    const companyPriorities = useCompanyOnboardingStore((state) => state.companyPriorities);

    const companyProfileIsComplete = companyDescription.isJust && companyPriorities.isJust;
    const employeeProfileIsActive = companyOnboardingStep === 'cv-upload';

    const employeeProfileLabel = useCompanyOnboardingStore((state) => state.employeeProfileLabel);
    const skillMosaicLabel = useCompanyOnboardingStore((state) => state.skillMosaicLabel);
    const reviewSkillMosaicLabel = useCompanyOnboardingStore((state) => state.reviewSkillMosaicLabel);

    return (
        <SidebarSection>
            <RootStep
                isActive={employeeProfileIsActive}
                isDisabled={companyProfileIsComplete === false}
                label={employeeProfileLabel}
                stepNumber={1}
            />
            <SidebarStep
                isActive={companyOnboardingStep === 'cv-upload'}
                isDisabled={companyProfileIsComplete === false}
                label={skillMosaicLabel}
            />
            <SidebarStep
                isActive={companyOnboardingStep === 'review-skills'}
                isDisabled={companyProfileIsComplete === false}
                label={reviewSkillMosaicLabel}
            />
        </SidebarSection>
    );
};

export const OnboardingSidebar: FunctionComponent = () => {
    const onboardingType = useCompanyOnboardingStore((state) => state.onboardingType);

    return (
        <SidebarShell>
            {onboardingType === 'company' ? <NavigationSidebar /> : <EmployeeNavigationSidebar />}
        </SidebarShell>
    );
};
